import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
  Grid,
  Paper,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckIcon from '@material-ui/icons/Check';
import { Requirement } from './types';
import type { FixSkippedSuggestion } from './api/api';
import { useSharedStyles } from './styles/shared';
import {
  TEXT_MAX,
  charCount,
} from '../requirement-lifecycle/metadataValidation';

const useStyles = makeStyles(theme => ({
  noChangeAlert: {
    marginBottom: theme.spacing(2),
  },
  skippedList: {
    margin: theme.spacing(1, 0, 0),
    paddingLeft: theme.spacing(2.5),
  },
  beforePaper: {
    backgroundColor: theme.palette.error.light,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  afterPaper: {
    backgroundColor: theme.palette.success.light,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  beforeLabel: {
    fontWeight: 600,
    color: theme.palette.error.dark,
  },
  afterLabel: {
    fontWeight: 600,
    color: theme.palette.success.dark,
  },
  diffText: {
    marginTop: theme.spacing(1),
    fontFamily: 'monospace',
    fontSize: '0.85rem',
  },
  editorField: {
    fontFamily: 'monospace',
    fontSize: '0.9rem',
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
}));

interface ManualEditorProps {
  requirement: Requirement;
  onApplyFix: (before: string, after: string) => void;
  fixedText?: string;
  // Localised explanation from the backend for why the requirement came back
  // unchanged. Preferred over the generic wording when present.
  fixNotes?: string;
  // Suggestions the fix could not apply. Listed with their reasons, since
  // fixNotes only says that nothing was applied, not why.
  skippedSuggestions?: FixSkippedSuggestion[];
  // Re-runs the backend fix and resolves with fresh corrected text.
  onRegenerate?: () => Promise<string | undefined>;
  // Turns the rewrite down. The caller rejects the pending proposal
  // (CB25SES-1567) and returns to the review screen; it may fail, in which
  // case this screen stays put.
  onCancel?: () => void | Promise<void>;
  // Whether a proposal is awaiting the user's decision. Drives the warning and
  // the Cancel label: without one, cancelling records nothing.
  hasPendingProposal?: boolean;
  // The text on screen is already saved and only the re-analysis is left,
  // because starting it failed. Accept then retries the analysis, so it stays
  // available although the text equals the requirement's.
  savedAwaitingAnalysis?: boolean;
}

const ManualEditor: React.FC<ManualEditorProps> = ({
  requirement,
  onApplyFix,
  fixedText: initialFixedText,
  fixNotes,
  skippedSuggestions = [],
  onRegenerate,
  onCancel,
  hasPendingProposal,
  savedAwaitingAnalysis = false,
}) => {
  const { t } = useTranslation('requirement-doctor');
  const shared = useSharedStyles();
  const classes = useStyles();

  // Use provided fixedText from S6 (Backend fix result), or fall back to the
  // requirement's own text so the editor is never empty.
  const fixedText = initialFixedText ?? requirement.text;
  const [editedText, setEditedText] = useState(fixedText);

  // The backend answers a fix request with the text unchanged when the analysis
  // produced no suggestion it could apply — a rule like TR-01 (traceability) is
  // reported as a violation but deliberately yields no suggestion, because it
  // cannot be judged from a single requirement. Without saying so, the two panes
  // below are identical and the screen looks broken. Compare the text the backend
  // returned, not editedText, so the notice does not flicker while the user types.
  const fixProducedNoChange =
    initialFixedText !== undefined && initialFixedText === requirement.text;
  const [regenerating, setRegenerating] = useState(false);
  const [declining, setDeclining] = useState(false);

  // Whether a proposal is actually awaiting a decision. The caller knows: it
  // only puts a rewrite up for review when the fix changed the text. Inferring
  // it here from the text alone would disagree with the caller the moment that
  // rule changes, so take it as given and fall back to the text comparison only
  // when the caller says nothing (older callers, and the tests for them).
  const hasRewriteToDecline =
    hasPendingProposal ??
    (initialFixedText !== undefined && initialFixedText !== requirement.text);

  const busy = regenerating || declining;

  // Accepting saves the text as a new version, so it has to differ from the
  // requirement's current text: an unchanged save would only add a duplicate
  // version and re-run the analysis on it. The exception is a text already
  // saved whose analysis did not start: Accept retries only the analysis.
  const trimmedEdit = editedText.trim();
  // The text is sent as typed, so the limit applies to it untrimmed.
  const editTooLong = charCount(editedText) > TEXT_MAX;
  const unchanged = trimmedEdit === requirement.text.trim();
  const canAccept =
    trimmedEdit.length > 0 &&
    (!unchanged || savedAwaitingAnalysis) &&
    !editTooLong;

  // One line per skipped suggestion: "title: reason". A suggestion reported
  // twice is listed once; distinct suggestions with the same reason each get a
  // line, so the count stays visible. Without a backend label the reason is
  // our own generic wording, never the raw reason code.
  const seenSuggestionIds = new Set<string>();
  const skippedLines = skippedSuggestions
    .filter(s => {
      if (seenSuggestionIds.has(s.suggestionId)) return false;
      seenSuggestionIds.add(s.suggestionId);
      return true;
    })
    .map(s => {
      const reason = s.reasonLabel || t('s7.beforeAfter.skippedReasonFallback');
      return {
        key: s.suggestionId,
        text: s.title ? `${s.title}: ${reason}` : reason,
      };
    });

  // Sync editor when the parent provides a new fixedText (e.g. after Regenerate
  // updates store.lastFixedText and re-renders ManualEditor with the fresh prop).
  useEffect(() => {
    setEditedText(initialFixedText ?? requirement.text);
  }, [initialFixedText, requirement.text]);

  const handleApply = () => {
    onApplyFix(requirement.text, editedText);
  };

  const handleCancel = async () => {
    if (!onCancel) return;
    setDeclining(true);
    try {
      await onCancel();
    } catch (err) {
      // The caller reports its own failures; this guard is for one that got
      // past it, which would otherwise leave the buttons disabled for good.
      console.error('Decline failed:', err);
    } finally {
      // The caller leaves this screen on success, so this only matters when it
      // failed and the user is still here.
      setDeclining(false);
    }
  };

  const handleRegenerate = async () => {
    if (!onRegenerate) return;
    setRegenerating(true);
    try {
      const fresh = await onRegenerate();
      if (fresh) {
        setEditedText(fresh);
      }
    } catch (err) {
      console.error('Regenerate failed:', err);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Grid container spacing={2}>
      {/* Top: Diff View */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" className={shared.sectionTitle}>
              {requirement.id} - {t('s7.beforeAfter.title')}
            </Typography>

            {(fixProducedNoChange || skippedLines.length > 0) && (
              <Alert severity="info" className={classes.noChangeAlert}>
                {/* The backend explains the outcome and is already localised;
                    fall back to the generic wording only when it sent nothing.
                    When the text did change, notes is empty and only some
                    suggestions were skipped, so a heading of our own leads the
                    list instead. */}
                {fixProducedNoChange
                  ? fixNotes || t('s7.beforeAfter.noChange')
                  : t('s7.beforeAfter.partiallySkipped')}
                {skippedLines.length > 0 && (
                  <ul className={classes.skippedList}>
                    {skippedLines.map(line => (
                      <li key={line.key}>{line.text}</li>
                    ))}
                  </ul>
                )}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper className={classes.beforePaper}>
                  <Typography variant="caption" className={classes.beforeLabel}>
                    {t('s7.beforeAfter.before')}
                  </Typography>
                  <Typography variant="body2" className={classes.diffText}>
                    {requirement.text}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper className={classes.afterPaper}>
                  <Typography variant="caption" className={classes.afterLabel}>
                    {t('s7.beforeAfter.after')}
                  </Typography>
                  <Typography variant="body2" className={classes.diffText}>
                    {editedText}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Bottom: Manual Edit Area */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" className={shared.sectionTitle}>
              {t('s7.title')}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={5}
              value={editedText}
              onChange={e => setEditedText(e.target.value)}
              placeholder={t('s7.editor.placeholder')}
              variant="outlined"
              className={classes.editorField}
              error={editTooLong}
              helperText={
                editTooLong
                  ? t('s7.editor.tooLong', { max: TEXT_MAX })
                  : undefined
              }
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Cancel is destructive now: say so before the user commits to it. */}
      {onCancel && hasRewriteToDecline && (
        <Grid item xs={12}>
          <Alert severity="warning">{t('s7.decline.warning')}</Alert>
        </Grid>
      )}

      {/* Action Buttons */}
      <Grid item xs={12}>
        <Box className={classes.actionButtons}>
          {onCancel && (
            <Button
              variant="outlined"
              startIcon={declining ? <CircularProgress size={18} /> : undefined}
              onClick={handleCancel}
              disabled={busy}
            >
              {hasRewriteToDecline
                ? t('s7.buttons.cancelAndDecline')
                : t('s7.buttons.cancel')}
            </Button>
          )}
          {onRegenerate && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                regenerating ? <CircularProgress size={18} /> : <RefreshIcon />
              }
              onClick={handleRegenerate}
              disabled={busy}
            >
              {t('s7.buttons.regenerate')}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<CheckIcon />}
            onClick={handleApply}
            disabled={busy || !canAccept}
          >
            {t('s7.buttons.accept')}
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ManualEditor;
